import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import TaskBasedQuoteBuilder from './TaskBasedQuoteBuilder';
import QuoteLibraryAdmin from './quote/QuoteLibraryAdmin';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Edit2, Trash2, Eye, FileText, Filter, Search, Calculator, Wrench, ArrowLeft, RefreshCw, FolderOpen, Loader2, BookOpen, Mail, Briefcase } from 'lucide-react';
import ConvertToWorkOrder from './quote/ConvertToWorkOrder';
import { toast } from 'sonner';
import JobMail from './mail/JobMail';
import { FY } from '../lib/fiscal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Quotes = () => {
  const { user } = useAuth();
  const currentYear = `FY${FY.long}`; // fiscal year — quotes numbered ${FY.short}-xxx
  const [quotes, setQuotes] = useState([]);
  const [enhancedQuotes, setEnhancedQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('search') || '');
  const [mailQuote, setMailQuote] = useState(null);
  const [convertQuote, setConvertQuote] = useState(null);
  const canConvert = (q) => /^\d{2}-\d{3,4}$/.test(String(q.quote_number || '')) && !q.project_number;
  const [statusFilter, setStatusFilter] = useState('all');
  const [estimatorFilter, setEstimatorFilter] = useState('all');
  const [thisYearOnly, setThisYearOnly] = useState(false);
  const estimatorDefaultedRef = useRef(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [taskQuoteEditing, setTaskQuoteEditing] = useState(null);  // ID of task quote being edited
  const [showAdvancedBuilder, setShowAdvancedBuilder] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') === '1') { setShowAdvancedBuilder(true); navigate('/quotes', { replace: true }); }
  }, [location.search]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [folderLoading, setFolderLoading] = useState(null);

  const openQuoteFolder = async (quoteNumber) => {
    if (!quoteNumber) { toast.error('This quote has no quote number to locate its folder'); return; }
    setFolderLoading(quoteNumber);
    try {
      const res = await axios.get(`${API}/quotes/${encodeURIComponent(quoteNumber)}/drive-folder`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const { url, found, level, name } = res.data || {};
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        if (!found) {
          toast.info(`No exact folder for ${quoteNumber} — opened ${name || 'parent folder'}`);
        }
      } else {
        toast.error('Could not resolve a Drive folder for this quote');
      }
    } catch (e) {
      toast.error('Failed to open the Drive folder');
    } finally {
      setFolderLoading(null);
    }
  };

  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    project_title: '',
    description: '',
    estimated_cost: '',
    valid_until: ''
  });

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      
      // First, trigger smart sync from Google Sheets
      try {
        const syncResponse = await axios.post(`${API}/quotes/sync-from-sheets`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (syncResponse.data.status === 'success' && syncResponse.data.quotes_synced > 0) {
          toast.success(`✅ Synced ${syncResponse.data.quotes_synced} quotes from Google Sheets`);
        } else if (syncResponse.data.status === 'no_changes') {
          console.log('📊 Quote Book: No changes detected');
        } else if (syncResponse.data.status === 'disabled') {
          console.log('⚠️ Quote Book sync not configured');
        }
      } catch (syncError) {
        console.warn('Quote sync warning:', syncError.response?.data?.detail || syncError.message);
      }
      
      // Then fetch all quotes
      const quotesResponse = await axios.get(`${API}/quotes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('Quotes response:', quotesResponse.data);
      
      // Ensure we have an array
      const quotesData = Array.isArray(quotesResponse.data) ? quotesResponse.data : [];
      console.log(`Loaded ${quotesData.length} quotes`);
      
      // Find quote 26-177
      const q177 = quotesData.find(q => q.quote_number === '26-177');
      if (q177) {
        console.log('✅ Found 26-177 in loaded data:', q177);
      } else {
        console.log('❌ 26-177 NOT in loaded data');
        console.log('Quote numbers starting with 26-1:', quotesData.filter(q => q.quote_number?.startsWith('26-1')).map(q => q.quote_number));
      }
      
      console.log('Setting quotes state and loading to false');
      
      setQuotes(quotesData);
      setEnhancedQuotes([]);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Failed to load quotes');
    } finally {
      console.log('Setting loading to FALSE in finally block');
      setLoading(false);
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        total_amount: parseFloat(formData.estimated_cost), // Changed from estimated_cost to total_amount
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null
      };
      
      // Remove estimated_cost since backend expects total_amount
      delete submitData.estimated_cost;

      // Create quote in 365Ops
      const response = await axios.post(`${API}/quotes`, submitData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.success('Quote created successfully');
      
      // Add quote to Google Sheets
      try {
        const quoteForSheets = {
          quote_number: response.data.id || `Q-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          client_name: submitData.client_name,
          project_description: submitData.description || submitData.project_title,
          location: submitData.location || '',
          amount: submitData.total_amount,
          status: 'pending',
          contact: submitData.client_name,
          phone: submitData.client_phone || '',
          email: submitData.client_email || '',
          notes: `Created in 365Ops - ${new Date().toLocaleDateString()}`
        };
        
        const sheetsResponse = await axios.post(`${API}/quotes/add-to-sheets`, quoteForSheets, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (sheetsResponse.data.status === 'success') {
          toast.success('✅ Quote added to Google Sheets');
        }
      } catch (sheetsError) {
        console.warn('Google Sheets sync warning:', sheetsError.response?.data?.detail || sheetsError.message);
        // Don't show error toast for sheets sync failure - quote was still created
      }
      
      setIsCreateOpen(false);
      resetForm();
      fetchQuotes(); // This will trigger another sync to get the latest data
    } catch (error) {
      console.error('Error creating quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to create quote');
    }
  };

  const handleEditQuote = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        total_amount: parseFloat(formData.estimated_cost), // Changed from estimated_cost to total_amount
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null
      };
      
      // Remove estimated_cost since backend expects total_amount
      delete submitData.estimated_cost;

      await axios.put(`${API}/quotes/${selectedQuote.id}`, submitData);
      toast.success('Quote updated successfully');
      setIsEditOpen(false);
      resetForm();
      fetchQuotes();
    } catch (error) {
      console.error('Error updating quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to update quote');
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    console.log('🔴 DELETE QUOTE REQUESTED:', quoteId);
    console.log('🔴 Quote ID type:', typeof quoteId);
    
    // Check BOTH quotes arrays since UI displays combined data
    const quoteInRegular = quotes.find(q => q.id === quoteId);
    const quoteInEnhanced = enhancedQuotes.find(q => q.id === quoteId);
    const quote = quoteInRegular || quoteInEnhanced;
    
    console.log('🔴 Found in regular quotes:', !!quoteInRegular);
    console.log('🔴 Found in enhanced quotes:', !!quoteInEnhanced);
    console.log('🔴 Found quote object:', quote);
    console.log('🔴 Regular quotes count:', quotes.length);
    console.log('🔴 Enhanced quotes count:', enhancedQuotes.length);
    console.log('🔴 Regular quote IDs:', quotes.map(q => q.id));
    console.log('🔴 Enhanced quote IDs:', enhancedQuotes.map(q => q.id));
    
    if (!quote) {
      console.log('🔴 QUOTE NOT FOUND IN EITHER STATE ARRAY - This quote may be stale');
      
      // Clean up stale quote from UI and refresh
      toast.warning('This quote may have already been deleted. Refreshing the list...');
      fetchQuotes(); // Refresh to get current data
      return;
    }
    
    console.log('🔴 Quote found, proceeding with deletion...');
    console.log('🔴 Quote._id field:', quote?._id);
    console.log('🔴 Quote.id field:', quote?.id);
    
    // Show custom confirmation dialog instead of window.confirm
    setQuoteToDelete(quoteId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteQuote = async () => {
    if (!quoteToDelete) return;
    
    console.log('🔴 USER CONFIRMED QUOTE DELETION:', quoteToDelete);
    
    // Check which array the quote is in to determine the correct API endpoint
    const quoteInRegular = quotes.find(q => q.id === quoteToDelete);
    const quoteInEnhanced = enhancedQuotes.find(q => q.id === quoteToDelete);
    
    let deleteUrl;
    if (quoteInRegular) {
      deleteUrl = `${API}/quotes/${quoteToDelete}`;
      console.log('🔴 Deleting from regular quotes endpoint');
    } else if (quoteInEnhanced) {
      // Use the correct enhanced quotes endpoint
      deleteUrl = `${API}/enhanced-quotes/${quoteToDelete}`;
      console.log('🔴 Deleting from enhanced quotes endpoint');
    } else {
      console.log('🔴 Quote not found in either array - trying regular endpoint as fallback');
      deleteUrl = `${API}/quotes/${quoteToDelete}`;
    }
    
    console.log('🔴 Delete URL will be:', deleteUrl);
    
    try {
      const response = await axios.delete(deleteUrl);
      console.log('🔴 DELETE SUCCESS:', response.status);
      toast.success('Quote deleted successfully');
      fetchQuotes(); // Refresh the list
    } catch (error) {
      console.error('🔴 Error deleting quote:', error);
      console.error('🔴 Error response status:', error.response?.status);
      console.error('🔴 Error response data:', error.response?.data);
      console.error('🔴 Quote ID that failed:', quoteToDelete);
      
      // If deletion failed, try alternative endpoints
      if (error.response?.status === 404) {
        console.log('🔴 First endpoint failed, trying alternative endpoints...');
        
        // Try alternative endpoints in order of preference
        const alternativeEndpoints = [];
        
        if (quoteInEnhanced) {
          alternativeEndpoints.push(`${API}/task-quotes/${quoteToDelete}`);
          alternativeEndpoints.push(`${API}/quotes/${quoteToDelete}`);
        } else {
          alternativeEndpoints.push(`${API}/enhanced-quotes/${quoteToDelete}`);
        }
        
        let fallbackSuccess = false;
        
        for (const endpoint of alternativeEndpoints) {
          try {
            console.log(`🔴 Trying alternative endpoint: ${endpoint}`);
            const fallbackResponse = await axios.delete(endpoint);
            console.log('🔴 ALTERNATIVE DELETE SUCCESS:', fallbackResponse.status);
            toast.success('Quote deleted successfully');
            fetchQuotes();
            fallbackSuccess = true;
            break;
          } catch (fallbackError) {
            console.log(`🔴 Alternative endpoint ${endpoint} failed:`, fallbackError.response?.status);
          }
        }
        
        if (fallbackSuccess) {
          setShowDeleteConfirm(false);
          setQuoteToDelete(null);
          return;
        }
        
        try {
          // Last resort fallback
          const fallbackResponse = await axios.delete(`${API}/quotes/${quoteToDelete}`);
          console.log('🔴 FALLBACK DELETE SUCCESS:', fallbackResponse.status);
          toast.success('Quote deleted successfully');
          fetchQuotes();
        } catch (fallbackError) {
          console.error('🔴 Fallback delete also failed:', fallbackError);
          if (fallbackError.response?.status === 404) {
            // Both endpoints returned 404 - this is a stale quote that doesn't exist in backend
            console.log('🔴 STALE QUOTE DETECTED - Removing from frontend state directly');
            
            // Remove the stale quote from frontend state
            if (quoteInRegular) {
              setQuotes(prev => prev.filter(q => q.id !== quoteToDelete));
            }
            if (quoteInEnhanced) {
              setEnhancedQuotes(prev => prev.filter(q => q.id !== quoteToDelete));
            }
            
            toast.success('Stale quote removed from display');
            console.log('🔴 Quote removed from frontend state');
          } else {
            toast.error('Failed to delete quote');
          }
        }
      } else if (error.response?.status === 404) {
        // Single endpoint returned 404 - also a stale quote
        console.log('🔴 STALE QUOTE DETECTED - Removing from frontend state directly');
        
        // Remove the stale quote from frontend state
        if (quoteInRegular) {
          setQuotes(prev => prev.filter(q => q.id !== quoteToDelete));
        }
        if (quoteInEnhanced) {
          setEnhancedQuotes(prev => prev.filter(q => q.id !== quoteToDelete));
        }
        
        toast.success('Stale quote removed from display');
        console.log('🔴 Quote removed from frontend state');
      } else {
        toast.error('Failed to delete quote');
      }
    }
    
    // Reset state
    setShowDeleteConfirm(false);
    setQuoteToDelete(null);
  };

  const cancelDeleteQuote = () => {
    console.log('🔴 USER CANCELLED QUOTE DELETION');
    setShowDeleteConfirm(false);
    setQuoteToDelete(null);
  };

  const handleStatusUpdate = async (quoteId, newStatus) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}`, { status: newStatus });
      toast.success('Quote status updated');
      fetchQuotes();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const openAdvancedBuilder = (quoteId = null) => {
    setEditingQuoteId(quoteId);
    setShowAdvancedBuilder(true);
  };

  const handleAdvancedQuoteSave = (savedQuote) => {
    setShowAdvancedBuilder(false);
    setEditingQuoteId(null);
    fetchQuotes();
  };

  const handleAdvancedQuoteCancel = () => {
    setShowAdvancedBuilder(false);
    setEditingQuoteId(null);
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      client_email: '',
      client_phone: '',
      project_title: '',
      description: '',
      estimated_cost: '',
      valid_until: ''
    });
    setSelectedQuote(null);
  };

  const startEdit = (quote) => {
    // Check if this is a 365ops quote
    if (quote.created_in_365ops) {
      // Open TaskBasedQuoteBuilder for editing
      setTaskQuoteEditing(quote.id);  // Pass quote ID, not full data
    } else {
      // Open regular edit modal
      setSelectedQuote(quote);
      setFormData({
        client_name: quote.client_name || '',
        client_email: quote.client_email || '',
        client_phone: quote.client_phone || '',
        project_title: quote.project_title || quote.title || '',
        description: quote.description || '',
        estimated_cost: (quote.total_amount || quote.estimated_cost || 0).toString(),
        valid_until: quote.valid_until ? new Date(quote.valid_until).toISOString().split('T')[0] : ''
      });
      setIsEditOpen(true);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      draft: 'status-draft',
      sent: 'status-sent',
      accepted: 'status-accepted',
      rejected: 'status-rejected',
      expired: 'status-rejected'
    };
    return `status-badge ${statusClasses[status] || 'status-draft'}`;
  };

  const getStatusClass = (status) => {
    const statusClasses = {
      draft: 'bg-gray-200 text-gray-700',
      sent: 'bg-blue-200 text-blue-800',
      pending: 'bg-yellow-200 text-yellow-800',
      accepted: 'bg-green-200 text-green-800',
      completed: 'bg-green-300 text-green-900',
      rejected: 'bg-red-200 text-red-800',
      expired: 'bg-red-200 text-red-800'
    };
    return statusClasses[status] || 'bg-gray-200 text-gray-700';
  };

  // Logged-in user's estimator initials (first + last name initial), e.g. "Steven Geisel" -> "SG"
  const myInitials = useMemo(() => {
    const parts = (user?.full_name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }, [user]);

  // Estimator column holds initials from the Quote Book ("JK", "JK/CL") but full names on 365Ops-built
  // quotes ("Justin Koleszar") — normalise both to initials so the filter matches either.
  const estimatorTokens = (raw) => (raw || '').split('/').map(tok => {
    const t = tok.trim();
    if (!t) return '';
    if (t.includes(' ')) {
      const parts = t.split(/\s+/).filter(Boolean);
      return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
    }
    return t.toUpperCase();
  }).filter(Boolean);

  // Distinct estimator tokens across all quotes (splits combos like "JK/CL"), normalized + sorted
  const estimatorOptions = useMemo(() => {
    const set = new Set();
    quotes.forEach(q => estimatorTokens(q.estimator).forEach(t => set.add(t)));
    return Array.from(set).sort();
  }, [quotes]);

  // Default the estimator filter to the logged-in PM/estimator (once, after quotes load)
  useEffect(() => {
    if (!estimatorDefaultedRef.current && quotes.length > 0 && estimatorOptions.length > 0) {
      estimatorDefaultedRef.current = true;
      if (myInitials && estimatorOptions.includes(myInitials) && !new URLSearchParams(window.location.search).get('search')) {
        setEstimatorFilter(myInitials);
      }
    }
  }, [quotes, estimatorOptions, myInitials]);

  // Show Advanced Quote Builder if requested — placed AFTER all hooks to satisfy the Rules of Hooks (fixes React #300)
  if (showAdvancedBuilder) {
    return (
      <TaskBasedQuoteBuilder
        editingQuoteId={editingQuoteId}
        onSave={handleAdvancedQuoteSave}
        onCancel={handleAdvancedQuoteCancel}
      />
    );
  }

  // Editing an existing 365ops quote takes over the page too (it used to render underneath the list, off-screen)
  if (taskQuoteEditing) {
    return (
      <TaskBasedQuoteBuilder
        editingQuoteId={taskQuoteEditing}
        onSave={() => {
          setTaskQuoteEditing(null);
          fetchQuotes();
        }}
        onCancel={() => setTaskQuoteEditing(null)}
      />
    );
  }

  // Combine all quotes for filtering
  // Backend already merged quotes, no need to merge again
  const filteredQuotes = quotes
    .filter(quote => {
      const matchesSearch = 
        (quote.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (quote.project_title || quote.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (quote.quote_number || quote.quote_id || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
      const estTokens = estimatorTokens(quote.estimator);
      const matchesEstimator = estimatorFilter === 'all' || estTokens.includes(estimatorFilter);
      const matchesYear = !thisYearOnly || String(quote.quote_number || quote.quote_id || '').startsWith(`${FY.short}-`);
      return matchesSearch && matchesStatus && matchesEstimator && matchesYear;
    })
    .sort((a, b) => {
      // Sort by year (newest first), then by quote number (highest first)
      const getYearAndNum = (quote) => {
        const qnum = quote.quote_number || quote.quote_id || '';
        if (qnum.includes('-')) {
          try {
            const parts = qnum.split('-');
            const year = parseInt(parts[0]) || 0; // "25" or "26"
            const num = parseInt(parts[1]) || 0;  // "541" or "001"
            return { year, num };
          } catch {
            return { year: 0, num: 0 };
          }
        }
        return { year: 0, num: 0 };
      };
      
      const aData = getYearAndNum(a);
      const bData = getYearAndNum(b);
      
      // Sort by year descending (26 before 25)
      if (bData.year !== aData.year) {
        return bData.year - aData.year;
      }
      
      // Within same year, sort by number descending (541 before 540)
      return bData.num - aData.num;
    });

  if (loading) {
    console.log('⏳ LOADING STATE = TRUE, showing skeletons');
    return (
      <div className="animate-fade-in">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Quotes</h1>
            <p className="text-slate-500">Manage your project quotes and proposals</p>
          </div>
          <div className="skeleton h-10 w-32"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card">
              <div className="card-body">
                <div className="skeleton h-4 w-full mb-2"></div>
                <div className="skeleton h-4 w-2/3 mb-4"></div>
                <div className="skeleton h-8 w-20"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {showLibrary && <QuoteLibraryAdmin onClose={() => setShowLibrary(false)} />}
      {convertQuote && (
        <ConvertToWorkOrder quote={convertQuote} onClose={() => setConvertQuote(null)} onConverted={() => fetchQuotes()} />
      )}
      {mailQuote && (
        <div className="fixed inset-0 z-[9000] bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setMailQuote(null)} data-testid="quote-mail-overlay">
          <div className="w-full max-w-6xl mt-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-end mb-2"><button onClick={() => setMailQuote(null)} className="text-slate-600 hover:text-slate-900 text-sm font-semibold" data-testid="quote-mail-close">Close ✕</button></div>
            <JobMail reference={mailQuote} />
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-slate-900">Quotes</h1>
          <p className="text-xs sm:text-sm text-slate-500">Manage your project quotes and proposals</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {['super_admin', 'office_admin', 'project_manager_estimator', 'fabrication_manager'].includes(user?.role) && (
            <Button
              onClick={() => setShowLibrary(true)}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-100 text-xs sm:text-sm"
              size="sm"
              data-testid="quote-library-btn"
            >
              <BookOpen className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Quote Library
            </Button>
          )}
          <Button
            onClick={() => { fetchQuotes(); toast.info('Refreshing...'); }}
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-100 text-xs sm:text-sm"
            size="sm"
          >
            <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            Refresh
          </Button>
          
          <Button
            onClick={() => { setQuotes([]); setEnhancedQuotes([]); fetchQuotes(); toast.info('Cleaning up...'); }}
            variant="outline" 
            className="border-yellow-600 text-yellow-700 hover:bg-yellow-50 text-xs sm:text-sm"
            size="sm"
          >
            <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            Clean Up
          </Button>
          
          <Button
            onClick={() => setShowAdvancedBuilder(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-xs sm:text-sm"
            size="sm"
            data-testid="new-task-quote-btn"
          >
            <Calculator className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            New Quote
          </Button>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                New Quote
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-slate-900">Create New Quote</DialogTitle>
              <DialogDescription className="text-slate-500">
                Create a new quote for a potential project
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateQuote} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client_name" className="text-slate-800">Client Name</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="client_email" className="text-slate-800">Client Email</Label>
                  <Input
                    id="client_email"
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client_phone" className="text-slate-800">Phone (Optional)</Label>
                  <Input
                    id="client_phone"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                  />
                </div>
                <div>
                  <Label htmlFor="estimated_cost" className="text-slate-800">Estimated Cost</Label>
                  <Input
                    id="estimated_cost"
                    type="number"
                    step="0.01"
                    value={formData.estimated_cost}
                    onChange={(e) => setFormData({...formData, estimated_cost: e.target.value})}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="project_title" className="text-slate-800">Project Title</Label>
                <Input
                  id="project_title"
                  value={formData.project_title}
                  onChange={(e) => setFormData({...formData, project_title: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-slate-800">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label htmlFor="valid_until" className="text-slate-800">Valid Until (Optional)</Label>
                <Input
                  id="valid_until"
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                />
              </div>

              <div className="flex justify-end space-x-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary">
                  Create Quote
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-600" />
          <Input
            placeholder="Search quotes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-slate-300 text-slate-900 placeholder-slate-400 rounded-xl focus:ring-2 focus:ring-blue-500 font-medium"
          />
        </div>
        <Select value={estimatorFilter} onValueChange={setEstimatorFilter}>
          <SelectTrigger data-testid="estimator-filter" className="w-44 bg-white border-slate-300 text-slate-900 rounded-xl">
            <SelectValue placeholder="Estimator" />
          </SelectTrigger>
          <SelectContent className="bg-white/95 backdrop-blur-xl border-slate-200">
            <SelectItem value="all">All Estimators</SelectItem>
            {estimatorOptions.map(e => (
              <SelectItem key={e} value={e}>{e}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 bg-white border-slate-300 text-slate-900 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white/95 backdrop-blur-xl border-slate-200">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          data-testid="this-year-toggle"
          onClick={() => setThisYearOnly(v => !v)}
          className={`rounded-xl font-semibold ${thisYearOnly ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'}`}
        >
          {currentYear} Only
        </Button>
      </div>

      {/* Quotes List */}
      {filteredQuotes.length === 0 ? (
        <Card className="card">
          <CardContent className="p-12 text-center">
            <FileText className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No quotes found</h3>
            <p className="text-slate-500 mb-6">
              {quotes.length === 0 ? 'Create your first quote to get started' : 'Try adjusting your search or filters'}
            </p>
            {quotes.length === 0 && (
              <Button onClick={() => setIsCreateOpen(true)} className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Quote
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white/60 backdrop-blur-xl border-slate-200">
          <CardContent className="p-0">
            {/* Table Header - desktop only */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-2 py-2 border-b border-blue-200/40 bg-blue-50/60 text-slate-800 text-xs font-semibold">
              <div className="col-span-2">Quote</div>
              <div className="col-span-1">Date</div>
              <div className="col-span-2">Client</div>
              <div className="col-span-2">Project</div>
              <div className="col-span-1">Est</div>
              <div className="col-span-1">Amount</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2">Actions</div>
            </div>
            
            {/* Table Rows */}
            <div className="max-h-[600px] overflow-y-auto">
              {filteredQuotes.map((quote, index) => {
                try {
                  const amount = quote.total_amount ?? quote.estimated_cost ?? quote.quote_amount ?? 0;
                  return (
                    <div key={quote.id} className={`border-b border-blue-100/50 hover:bg-blue-50/40 transition-colors text-xs ${index % 2 === 0 ? 'bg-white/40' : 'bg-slate-900/10'}`}>
                  {/* Mobile card */}
                  <div className="md:hidden p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-slate-900">{quote.quote_id || quote.quote_number}</span>
                      <div className="flex items-center gap-2">
                        {quote.project_number && <button onClick={() => navigate(`/projects?q=${encodeURIComponent(quote.project_number)}`)} className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold font-mono" title="Open the work order" data-testid={`quote-wo-chip-mobile-${quote.quote_number}`}>WO {quote.project_number}</button>}
                        {canConvert(quote) && <button onClick={() => setConvertQuote(quote)} className="text-emerald-700 p-1 rounded hover:bg-emerald-50" title="Convert to work order" data-testid={`quote-convert-btn-mobile-${quote.quote_number}`}><Briefcase className="h-4 w-4" /></button>}
                        <button onClick={() => setMailQuote(quote.quote_number || quote.quote_id)} className="text-sky-600 p-1 rounded hover:bg-sky-50" title="Mail about this quote" data-testid={`quote-mail-btn-mobile-${quote.quote_number || quote.quote_id}`}>
                          <Mail className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openQuoteFolder(quote.quote_number || quote.quote_id)}
                          disabled={folderLoading === (quote.quote_number || quote.quote_id)}
                          className="text-amber-600 p-1 rounded hover:bg-amber-50"
                          title="Open Drive folder"
                          data-testid={`quote-folder-btn-mobile-${quote.quote_number || quote.quote_id}`}
                        >
                          {folderLoading === (quote.quote_number || quote.quote_id)
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <FolderOpen className="h-4 w-4" />}
                        </button>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${getStatusClass(quote.status)}`}>{quote.status || 'draft'}</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-900 font-semibold">{quote.client_name}</p>
                    <p className="text-xs text-slate-600">{quote.description || quote.project_title || quote.title || ''}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">{quote.estimator || ''}{quote.quote_date_display ? ` · ${quote.quote_date_display}` : ''}</span>
                      <span className="text-sm font-bold text-slate-900">{amount > 0 ? `$${Math.round(amount).toLocaleString()}` : 'TBC'}</span>
                    </div>
                  </div>

                  {/* Desktop grid */}
                  <div className="hidden md:grid grid-cols-12 gap-2 px-2 py-2">
                  {/* Quote Number */}
                  <div className="col-span-2">
                    <span className="text-slate-900 font-bold font-mono text-xs">
                      {quote.quote_id || quote.quote_number || `Q${quote.id?.substring(0, 6)}`}
                    </span>
                  </div>

                  {/* Date created (quote-book column D) */}
                  <div className="col-span-1">
                    <span className="text-slate-600 text-xs" title="Date quote was created">
                      {quote.quote_date_display || '—'}
                    </span>
                  </div>
                  
                  {/* Client - Compact */}
                  <div className="col-span-2">
                    <p className="text-slate-900 font-semibold text-xs truncate" title={quote.client_name}>
                      {quote.client_name}
                    </p>
                  </div>
                  
                  {/* Project - Compact */}
                  <div className="col-span-2">
                    <p className="text-slate-700 text-xs truncate" title={quote.description || quote.project_title || quote.title}>
                      {quote.description || quote.project_title || quote.title || 'No title'}
                    </p>
                  </div>
                  
                  {/* Estimator - Compact */}
                  <div className="col-span-1">
                    <p className="text-slate-600 text-xs font-medium">
                      {quote.estimator || 'N/A'}
                    </p>
                  </div>
                  
                  {/* Amount - Compact */}
                  <div className="col-span-1">
                    <p className="text-slate-900 font-semibold text-xs">
                      {(() => {
                        const amount = quote.total_amount ?? quote.estimated_cost ?? quote.quote_amount ?? 0;
                        return amount > 0 ? `$${Math.round(amount).toLocaleString()}` : 'TBC';
                      })()}
                    </p>
                  </div>
                  
                  {/* Status - Compact */}
                  <div className="col-span-1">
                    {quote.project_number ? (
                      <button onClick={() => navigate(`/projects?q=${encodeURIComponent(quote.project_number)}`)} className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold font-mono hover:bg-emerald-200" title="Open the work order" data-testid={`quote-wo-chip-${quote.quote_number}`}>WO {quote.project_number}</button>
                    ) : (
                      <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${getStatusClass(quote.status)}`}>
                        {quote.status || 'draft'}
                      </span>
                    )}
                  </div>
                  
                  {/* Actions - Smaller buttons */}
                  <div className="col-span-2 flex gap-1">
                    {canConvert(quote) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConvertQuote(quote)}
                        className="h-6 w-6 p-0 text-emerald-700 hover:bg-emerald-50"
                        title="Convert to work order"
                        data-testid={`quote-convert-btn-${quote.quote_number}`}
                      >
                        <Briefcase className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setMailQuote(quote.quote_number || quote.quote_id)}
                      className="h-6 w-6 p-0 text-sky-600 hover:bg-sky-50"
                      title="Mail about this quote"
                      data-testid={`quote-mail-btn-${quote.quote_number || quote.quote_id}`}
                    >
                      <Mail className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openQuoteFolder(quote.quote_number || quote.quote_id)}
                      disabled={folderLoading === (quote.quote_number || quote.quote_id)}
                      className="h-6 w-6 p-0 text-amber-600 hover:bg-amber-50"
                      title="Open Drive folder"
                      data-testid={`quote-folder-btn-${quote.quote_number || quote.quote_id}`}
                    >
                      {folderLoading === (quote.quote_number || quote.quote_id)
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <FolderOpen className="h-3 w-3" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(quote)}
                      className="h-6 w-6 p-0 text-blue-600 hover:bg-blue-50"
                      title="Edit"
                      data-testid={`quote-edit-${quote.quote_number || quote.id}`}
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteQuote(quote.id)}
                      className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  </div>
                </div>
                  );
                } catch (error) {
                  console.error(`Error rendering quote at index ${index}:`, quote, error);
                  return null;
                }
              })}
            </div>
            
            {/* Footer with count */}
            <div className="p-4 bg-white border-t border-slate-200">
              <p className="text-slate-500 text-sm">
                Showing {filteredQuotes.length} of {quotes.length} quotes
                {estimatorFilter !== 'all' ? ` · Estimator: ${estimatorFilter}` : ''}
                {thisYearOnly ? ` · ${currentYear} only` : ''}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog - Brightened for non-365ops quotes */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Edit Quote</DialogTitle>
            <DialogDescription className="text-gray-600">
              Update quote information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditQuote} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_client_name" className="text-gray-700">Client Name</Label>
                <Input
                  id="edit_client_name"
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  className="bg-white border-gray-300 text-gray-900"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_client_email" className="text-gray-700">Client Email</Label>
                <Input
                  id="edit_client_email"
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                  className="bg-white border-gray-300 text-gray-900"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_client_phone" className="text-gray-700">Phone (Optional)</Label>
                <Input
                  id="edit_client_phone"
                  value={formData.client_phone}
                  onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
              <div>
                <Label htmlFor="edit_estimated_cost" className="text-gray-700">Estimated Cost</Label>
                <Input
                  id="edit_estimated_cost"
                  type="number"
                  step="0.01"
                  value={formData.estimated_cost}
                  onChange={(e) => setFormData({...formData, estimated_cost: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit_project_title" className="text-slate-800">Project Title</Label>
              <Input
                id="edit_project_title"
                value={formData.project_title}
                onChange={(e) => setFormData({...formData, project_title: e.target.value})}
                className="bg-slate-100 border-slate-300 text-slate-900"
                required
              />
            </div>

            <div>
              <Label htmlFor="edit_description" className="text-slate-800">Description</Label>
              <Textarea
                id="edit_description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-slate-100 border-slate-300 text-slate-900"
                rows={3}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit_valid_until" className="text-slate-800">Valid Until (Optional)</Label>
              <Input
                id="edit_valid_until"
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                className="bg-slate-100 border-slate-300 text-slate-900"
              />
            </div>

            <div className="flex justify-end space-x-4">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Update Quote
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Custom Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white border border-slate-300 rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Confirm Deletion</h3>
            <p className="text-slate-700 mb-6">
              Are you sure you want to delete this quote? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDeleteQuote}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Yes, Delete
              </button>
              <button
                onClick={cancelDeleteQuote}
                className="flex-1 bg-slate-200 hover:bg-slate-100 text-slate-900 py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quotes;